/**
 * One mapper for every sign-in failure: the human gets copy that names the
 * actual problem (and never blames their password for our infrastructure),
 * the engineer gets a stable `code` + `ourFault` split so Rollbar carries
 * real failures grouped by auth code instead of one generic bucket.
 *
 * Born 2026-07-31: App Check enforcement 401'd a real user at /signin and the
 * UI said "An error occurred during sign in. Please try again." — retrying
 * could never work, and nothing distinguished it from a typo'd password.
 */

export interface MappedAuthError {
  /** Shown to the user in the sign-in alert. */
  message: string;
  /** Stable snake_case bucket for AUTH_ERROR analytics. */
  type: string;
  /** The auth/* code (without prefix) for Rollbar grouping; 'unknown' if none. */
  code: string;
  /**
   * true = our layer failed (security check, server, bug) → report loudly.
   * false = user-side outcome (typo, cancel, blocker) → breadcrumb only.
   */
  ourFault: boolean;
}

/** Pull the `auth/...` code off a FirebaseError, or fish it out of the message. */
export const extractAuthCode = (error: unknown): string => {
  const code = (error as { code?: unknown })?.code;
  if (typeof code === 'string' && code.startsWith('auth/')) {
    return code.slice('auth/'.length).replace(/\.$/, '');
  }
  const message = error instanceof Error ? error.message : String(error ?? '');
  const match = message.match(/auth\/([a-z0-9-]+)/i);
  return match ? match[1].toLowerCase() : 'unknown';
};

export const mapAuthError = (error: unknown): MappedAuthError => {
  const code = extractAuthCode(error);

  switch (code) {
    // Enumeration-safe: one message for every credential miss, so the copy
    // never reveals whether the email exists.
    case 'invalid-credential':
    case 'invalid-login-credentials':
    case 'wrong-password':
    case 'user-not-found':
      return {
        code,
        type: 'invalid_credentials',
        ourFault: false,
        message: "That email and password don't match. Double-check them, or use Forgot? to reset your password.",
      };

    case 'invalid-email':
      return {
        code,
        type: 'invalid_email',
        ourFault: false,
        message: "That doesn't look like a valid email address. Check for typos and try again.",
      };

    case 'user-disabled':
      return {
        code,
        type: 'user_disabled',
        ourFault: false,
        message: 'This account has been disabled. Contact support@socialworktestprepacademy.com and we will sort it out.',
      };

    case 'too-many-requests':
      return {
        code,
        type: 'rate_limited',
        ourFault: false,
        message: 'Too many attempts in a row. Wait a few minutes and try again, or reset your password now.',
      };

    case 'network-request-failed':
      return {
        code,
        type: 'network_error',
        ourFault: false,
        message: "We couldn't reach the server. Check your connection and try again.",
      };

    // The 2026-07-31 incident: attestation failed, credentials never even
    // got checked. Say so — "try again" alone can't fix this one.
    case 'firebase-app-check-token-is-invalid':
    case 'firebase-app-check-token-is-invalid.':
      return {
        code: 'firebase-app-check-token-is-invalid',
        type: 'app_check_blocked',
        ourFault: true,
        message: 'Our security check blocked this sign-in. Your password is fine. Turn off ad or content blockers for this site, or try a different browser, and try again.',
      };

    case 'popup-closed-by-user':
    case 'cancelled-popup-request':
    case 'user-cancelled':
      return {
        code,
        type: 'popup_cancelled',
        ourFault: false,
        message: "Sign-in was cancelled. Try again whenever you're ready.",
      };

    case 'popup-blocked':
      return {
        code,
        type: 'popup_blocked',
        ourFault: false,
        message: 'Your browser blocked the sign-in window. Allow popups for this site and try again.',
      };

    case 'account-exists-with-different-credential':
      return {
        code,
        type: 'account_exists_different_credential',
        ourFault: false,
        message: 'This email already signs in a different way. Try Google or Apple, or use your email and password.',
      };

    default:
      return {
        code,
        type: 'unknown_error',
        ourFault: true,
        message: 'Something went wrong on our side, not yours. Give it a moment and try again. We have been notified.',
      };
  }
};
